import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import "../../../i18n";
import { Notice, XmlView } from "../../../design/components";
import { useDocumentXml, useMe } from "../../../api/queries";
import type { DocumentDetail } from "../../../api/types";
import { MappedPane } from "./MappedPane";
import { RawPane } from "./RawPane";
import { QrPanel } from "./QrPanel";
import styles from "./DocumentInspector.module.css";

type TabId = "raw" | "mapped" | "xml" | "qr";

export const REASON_MAX_LENGTH = 200;

/** m6-rulings.md #144: the source payload and XML are audited reads, so the admin is asked why first; blank is allowed. */
function AccessReasonPrompt({ onOpen }: { onOpen: (reason: string) => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onOpen(reason.trim());
  }

  return (
    <form className="stack" onSubmit={submit}>
      <p className={styles.note}>
        {t("documents.inspector.payload.accessNotice", "Opening the source payload or XML is recorded. Say why, if you can.")}
      </p>
      <label className="field" htmlFor="access-reason">
        <span>{t("documents.inspector.payload.reasonLabel", "Reason for viewing (optional)")}</span>
        <input
          id="access-reason"
          type="text"
          maxLength={REASON_MAX_LENGTH}
          autoComplete="off"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </label>
      <div>
        <button className="btn btn-sm" type="submit">
          {t("documents.inspector.payload.open", "Open")}
        </button>
      </div>
    </form>
  );
}

function XmlPane({ docId, sha256, reason }: { docId: string; sha256: string | null; reason: string }) {
  const { t } = useTranslation();
  const xml = useDocumentXml(docId, reason || undefined);
  const [href, setHref] = useState<string | null>(null);

  useEffect(() => {
    if (!xml.data) return;
    const url = URL.createObjectURL(new Blob([xml.data], { type: "application/xml" }));
    setHref(url);
    return () => URL.revokeObjectURL(url);
  }, [xml.data]);

  if (xml.isError) return <Notice kind="fail">{t("documents.inspector.payload.xmlLoadError", "The XML could not be loaded.")}</Notice>;
  if (!xml.data) return <p className={styles.sub}>{t("documents.inspector.payload.loading", "Loading…")}</p>;

  const download = href ? (
    <a className="btn btn-sm btn-ghost" href={href} download={`${docId}.xml`}>
      {t("documents.inspector.payload.downloadXml", "Download XML")}
    </a>
  ) : null;

  return (
    <>
      <div className={styles.paneHead}>
        {download}
        {sha256 ? <span className={styles.sha}>{sha256}</span> : null}
      </div>
      <div className={styles.pane}>
        <XmlView src={xml.data} />
      </div>
    </>
  );
}

/** Panes stay mounted once opened, so re-selecting a tab makes no second request. */
export function PayloadTabs({
  docId,
  artifacts,
}: {
  docId: string;
  artifacts: DocumentDetail["artifacts"];
}) {
  const { t } = useTranslation();
  const TABS: { id: TabId; label: string }[] = [
    { id: "raw", label: t("documents.inspector.payload.tabs.raw", "Raw") },
    { id: "mapped", label: t("documents.inspector.payload.tabs.mapped", "Mapped") },
    { id: "xml", label: t("documents.inspector.payload.tabs.xml", "XML") },
    { id: "qr", label: t("documents.inspector.payload.tabs.qr", "QR") },
  ];
  const [active, setActive] = useState<TabId>("raw");
  const [opened, setOpened] = useState<TabId[]>(["raw"]);
  // null until the admin has answered the prompt once; the answer covers both audited panes
  const [reason, setReason] = useState<string | null>(null);
  // Only a group admin's read is audited (payload_ctx), so only they are asked; until the
  // role is known the prompt stays up rather than opening an audited read unasked.
  const me = useMe();
  const audited = !me.data || me.data.role === "group_admin";
  const access = audited ? reason : "";

  const xmlSha = useMemo(
    () => artifacts.find((artifact) => artifact.kind === "xml")?.sha256 ?? null,
    [artifacts],
  );

  function select(id: TabId) {
    setActive(id);
    setOpened((current) => (current.includes(id) ? current : [...current, id]));
  }

  return (
    <section className="card">
      <div className="card-head">
        <div className={`grow ${styles.tabs}`} role="tablist" aria-label={t("documents.inspector.payload.tabsAriaLabel", "Payloads")}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`payload-tab-${tab.id}`}
              className={styles.tab}
              aria-selected={active === tab.id}
              aria-controls={`payload-pane-${tab.id}`}
              onClick={() => select(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {opened.map((id) => (
        <div
          key={id}
          className="card-body"
          id={`payload-pane-${id}`}
          role="tabpanel"
          aria-labelledby={`payload-tab-${id}`}
          hidden={active !== id}
        >
          {(id === "raw" || id === "xml") && access === null ? <AccessReasonPrompt onOpen={setReason} /> : null}
          {id === "raw" && access !== null ? <RawPane docId={docId} reason={access} /> : null}
          {id === "mapped" ? <MappedPane docId={docId} /> : null}
          {id === "xml" && access !== null ? <XmlPane docId={docId} sha256={xmlSha} reason={access} /> : null}
          {id === "qr" ? <QrPanel docId={docId} /> : null}
        </div>
      ))}
    </section>
  );
}
